import { Suspense, useEffect, useState } from 'react';
import { DmnViewerLazy } from '../dmn/DmnViewerLazy';
import { parseDmnDefinitions } from '../dmn/model';
import type { DmnDefinitionsModel, DmnKnowledgeSource } from '../dmn/types';
import { Badge } from '../components/Badge';
import { Card } from '../components/Card';
import { Spinner } from '../components/Spinner';
import { Tabs } from '../components/Tabs';
import type { Definition } from '../api/types';
import type { DmnView } from '../app/router';

export interface DecisionsTabProps {
  definition: Definition;
  decisionKey: string | null;
  view: DmnView;
  onSelectDecision(decisionKey: string): void;
  onSelectView(view: DmnView): void;
}

/**
 * DMNの判定表と出典。
 * 表示するのは常に「現在の定義」であり、過去のrunが実際に評価したDMNではない。
 */
export function DecisionsTab(props: DecisionsTabProps) {
  const decisions = props.definition.decisions;
  const current = decisions.find((entry) => entry.key === props.decisionKey) ?? decisions[0] ?? null;
  const [model, setModel] = useState<DmnDefinitionsModel | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);

  useEffect(() => {
    if (!current) return;
    let active = true;
    setModel(null);
    setParseError(null);
    parseDmnDefinitions(current.xml)
      .then((parsed) => {
        if (active) setModel(parsed);
      })
      .catch((error: unknown) => {
        if (active) setParseError(error instanceof Error ? error.message : String(error));
      });
    return () => {
      active = false;
    };
  }, [current]);

  if (!current) {
    return <p className="hint">このmicroappにはDMNがありません。</p>;
  }

  return (
    <div className="decisions-tab">
      {/* 入口が実行詳細でもapp詳細でも、GET A/definition が返すのは常に現在の定義。 */}
      <p className="notice">
        現在の定義を表示しています。過去のrunが実際に評価したDMNの根拠ではありません。
      </p>

      <div className="process-toolbar">
        <label className="field inline">
          <span className="field-label">判定</span>
          <select
            className="input"
            value={current.key}
            onChange={(event) => props.onSelectDecision(event.target.value)}
          >
            {decisions.map((entry) => (
              <option key={entry.key} value={entry.key}>
                {entry.key}
              </option>
            ))}
          </select>
        </label>
        <Tabs
          label="DMNの表示"
          active={props.view}
          onSelect={(key) => props.onSelectView(key as DmnView)}
          items={[
            { key: 'table', label: '判定表' },
            { key: 'drd', label: '根拠の関連図（DRD）' },
          ]}
        />
      </div>

      {parseError && <p className="failure">DMNを解析できませんでした: {parseError}</p>}
      {!model && !parseError && <Spinner label="DMNを解析しています" />}

      <div className="dmn-layout">
        <div className="dmn-area">
          <Suspense fallback={<Spinner label="判定表を読み込んでいます" />}>
            {/* 表示できない理由はDmnViewerが自分で描く。ここで二重に出さない。 */}
            <DmnViewerLazy xml={current.xml} view={props.view} />
          </Suspense>
        </div>

        {model && (
          <aside className="inspector">
            <KnowledgeSources model={model} />
            {model.warnings.length > 0 && (
              <Card title={`定義の警告 ${model.warnings.length}件`}>
                <ul className="warnings">
                  {model.warnings.map((warning, index) => (
                    <li key={`${warning.code}-${index}`}>
                      <code>{warning.code}</code> {warning.message}
                    </li>
                  ))}
                </ul>
              </Card>
            )}
          </aside>
        )}
      </div>

      {model && <RuleRationale model={model} />}
    </div>
  );
}

function KnowledgeSources({ model }: { model: DmnDefinitionsModel }) {
  const sources = Object.values(model.knowledgeSources);
  return (
    <section className="provenance-section">
      <h3>出典（knowledgeSource）</h3>
      {sources.length === 0 ? (
        <p className="hint">出典（knowledgeSource）なし</p>
      ) : (
        <ul className="knowledge-sources">
          {sources.map((source) => (
            <li key={source.id}>
              <KnowledgeSourceItem source={source} />
              <p className="hint">
                参照している判定:{' '}
                {model.decisions
                  .filter((decision) => decision.knowledgeSourceIds.includes(source.id))
                  .map((decision) => decision.id)
                  .join(', ') || '未参照'}
              </p>
            </li>
          ))}
        </ul>
      )}

      <h3>判定ごとの出典</h3>
      <ul className="knowledge-sources">
        {model.decisions.map((decision) => (
          <li key={decision.id}>
            <p className="knowledge-source-name">
              <code>{decision.id}</code>
            </p>
            {decision.knowledgeSourceIds.length === 0 && (
              <p className="hint">
                <Badge tone="unknown">出典未記載</Badge> authorityRequirementがありません。
              </p>
            )}
            {decision.danglingAuthorityRefs.map((ref) => (
              <p key={ref} className="hint">
                <Badge tone="failed">参照先不明</Badge> <code>{ref}</code>{' '}
                に対応するknowledgeSourceがありません。
              </p>
            ))}
          </li>
        ))}
      </ul>
    </section>
  );
}

function KnowledgeSourceItem({ source }: { source: DmnKnowledgeSource }) {
  return (
    <div className="knowledge-source">
      <p className="knowledge-source-name">
        {source.unknown && <Badge tone="unknown">出典不明</Badge>} {source.name}{' '}
        <code>{source.id}</code>
      </p>
      {source.locationURI && <LocationUri uri={source.locationURI} />}
      {source.description && <p className="provenance-text">{source.description}</p>}
    </div>
  );
}

/** http/httpsだけ別タブで開く。他のURIは文字列表示に留め、基盤から取得しない。 */
function LocationUri({ uri }: { uri: string }) {
  const isWeb = /^https?:\/\//i.test(uri);
  if (!isWeb) return <p className="mono hint">{uri}</p>;
  return (
    <p className="mono">
      <a href={uri} target="_blank" rel="noreferrer noopener">
        {uri}
      </a>
    </p>
  );
}

/** 行ごとの根拠。annotationEntryにidが無いのでrule idで行を識別する。 */
function RuleRationale({ model }: { model: DmnDefinitionsModel }) {
  return (
    <>
      {model.decisions.map((decision) => {
        if (!decision.table) return null;
        const labels = decision.table.annotationLabels;
        return (
          <Card key={decision.id} title={`行の根拠: ${decision.name || decision.id}`}>
            {labels.length === 0 && (
              <p className="hint">
                この判定表にはannotation列がありません（行ごとの根拠が書かれていません）。
              </p>
            )}
            <table className="rule-rationale">
              <thead>
                <tr>
                  <th scope="col">rule</th>
                  <th scope="col">条件</th>
                  <th scope="col">結果</th>
                  {labels.map((label) => (
                    <th key={label} scope="col">
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {decision.table.rules.map((rule) => (
                  <tr key={rule.id}>
                    <td>
                      <code>{rule.id}</code>
                    </td>
                    <td className="mono">{rule.inputEntries.join(' / ')}</td>
                    <td className="mono">{rule.outputEntries.join(' / ')}</td>
                    {labels.map((label, index) => (
                      <td key={label}>{rule.annotations[index] || '（未記載）'}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        );
      })}
    </>
  );
}
